// © Microsoft Corporation. All rights reserved.

import { CommentConnector, ComponentConnector, FollowInfo, PopupConnector, ProfileConnector, UsersConnector } from "../../Social.props";
import { Comments, User } from "@msnews/social-data-service";
import { ProfileCommentsTelemetryObjects, profileCommentsTelemetryContracts } from "./ProfileComments.telemetry";

import ProfileCommentItem from "./ProfileCommentItem";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { SocialState } from "../../Social.state";
import SortBox from "../../common/sort-box/SortBox";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { addOrUpdateTelemetryObject } from "../../Social.telemetry";
import { classNames } from "@microsoft/fast-web-utilities";
import { connect } from "react-redux";
import { enums } from "../../utils/enums";
import { hasThemeBase } from "../../utils/themeHelpers";
import { profileImages } from "../../utils/images";

export interface ProfileCommentsProps {
    componentConnector: ComponentConnector;
    comments: Comments;
    user: User;
    t: SocialLocalizedStrings;
    me: User;
    managedClasses?: SocialClassNameContract;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    profileConnector: ProfileConnector;
    commentConnector: CommentConnector;
    usersConnector: UsersConnector;
    market: string;
    popupConnector: PopupConnector;
    mainTelemetryObject?: TelemetryObjectBase;
    theme?: string;
    responsiveClass?: string;
}

interface ProfileCommentsState {
    sortType: string
}

const mapStateToProps = function(state: SocialState) {
    return {
        theme: state.theme
    };
};

export class ProfileComments extends React.Component<ProfileCommentsProps, ProfileCommentsState> {
    profileCommentListContainerRef: React.RefObject<HTMLUListElement>;
    isLoading: boolean;
    telemetryObjects: ProfileCommentsTelemetryObjects;
    static sortTypes = {
        Rating: "CommentSortByTopResponseTxt",
        Time: "CommentSortByNewestTxt",
        "Time asc": "CommentSortByOldestTxt"
    };

    constructor(props) {
        super(props);
        this.profileCommentListContainerRef = React.createRef();
        this.state = {
            sortType: "Time"
        };
    }

    async handleScroll() {
        const elem = this.profileCommentListContainerRef.current;
        if (!this.props.user || !this.props.user.id || !elem || this.isLoading || !this.props.comments || !this.props.comments.moreCount) {
            return;
        }
        const scrollPosition = elem.offsetHeight - window.pageYOffset - window.innerHeight;
        if (scrollPosition < 100) {
            this.isLoading = true;
            await this.props.profileConnector.fetchUserComments(this.props.user.id, false, this.state.sortType);
            this.isLoading = false;
        }
    }

    async componentDidMount() {
        window.addEventListener("scroll", this.handleScroll.bind(this));
        if (!this.props.user || !this.props.user.id) {
            return;
        }
        this.isLoading = true;
        await this.props.profileConnector.fetchUserComments(this.props.user.id, false, this.state.sortType);
        this.isLoading = false;
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleScroll.bind(this));
    }

    render() {
        const {componentConnector, comments, user, t, me, managedClasses, users, usersConnector, commentConnector, market, popupConnector, mainTelemetryObject, theme, responsiveClass} = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        let userId = undefined;
        if (user) { userId = user.id; }

        if (!user) { return null; }
        const isSelf = me && me.id == user.id;

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            mainItem: addOrUpdateTelemetryObject(mainTelemetryObject, profileCommentsTelemetryContracts.mainItem, {id: user.traceId}, this.telemetryObjects && this.telemetryObjects.mainItem)

        };

        return (
            (comments && comments.list.length > 0)
                ? <div className={classNames(
                    managedClasses.profileCommentsContainer,
                    [managedClasses.profileCommentsSuperAppContainer, isSuperApp],
                    responsiveClass
                )}>
                    { !isSuperApp && <SortBox {...{t, managedClasses, mainTelemetryObject: this.telemetryObjects.mainItem, selectedSortType: this.state.sortType, sortTypes: ProfileComments.sortTypes, id: userId}} onChange={this.getSortedComments.bind(this)}/> }
                    <ul className="cwt-profile-comment-item-list" ref={this.profileCommentListContainerRef}>
                        {comments && comments.list && comments.list.map((comment) => <ProfileCommentItem key={`ProfileCommentItem-${comment.id}`} {...{componentConnector, comment, user, me, managedClasses, t, users, usersConnector, commentConnector, market, popupConnector, mainTelemetryObject, theme, responsiveClass}}/>)}
                    </ul>
                </div>
                : <div className={classNames(
                    managedClasses.profileEmptyTabContainer,
                    [managedClasses.profileEmptyTabSuperAppContainer, isSuperApp]
                )}>
                    <img src={profileImages.zeroComments} alt={t.ImgAltsNoComments}/>
                    <div className="cwt-message"> {isSelf ? `${t.ProfilePageCommentsNoCommentForUserTitle}`: `${t.ProfilePageCommentsNoCommentForOthersTitle}`} </div>
                    <div className="cwt-sub-message"> {isSelf ? `${t.ProfilePageCommentsNoCommentForUserText}` : `${t.ProfilePageCommentsNoCommentForOthersText}`} </div>
                </div>
        );
    }

    getSortedComments(sortType) {
        this.setState({sortType});
        this.props.profileConnector.fetchUserComments(this.props.user.id, true, sortType);
    }
}

export default connect(mapStateToProps)(ProfileComments);